// =====================================================================
// Faz AD (2026-05-07) — System Health + Backup Dashboard
// =====================================================================
// Owner için "platformum sağlıklı mı, yedek alıyor muyum" cevabı.
//
// 3 panel:
//   1. Sistem durumu — entegrasyon + worker + DB stats
//      (mevcut /api/admin/system/status endpoint)
//   2. Backup durumu — son yedekler + boyut + başarı oranı
//      (yeni /api/admin/system/backup-status)
//   3. Manuel backup tetik butonu (acil durum)
//
// Backup felaket kurtarma için kritik. VPS crash, MariaDB bozulma,
// silme hatası vs. olursa 30 günlük yedek var.
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, Button
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BackupIcon from "@mui/icons-material/Backup";
import StorageIcon from "@mui/icons-material/Storage";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import BugReportIcon from "@mui/icons-material/BugReport";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import API from "../services/api";
import toast from "react-hot-toast";

// Bayt boyutu → okunur format (KB/MB/GB)
const fmtBytes = (b) => {
  const n = Number(b || 0);
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024)      return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
};

const fmtDate = (s) => s ? new Date(s).toLocaleString("tr-TR", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
}) : "—";

const fmtDuration = (ms) => {
  if (!ms) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}sn`;
  return `${(ms / 60000).toFixed(1)}dk`;
};

export default function SystemHealth() {
  const [status, setStatus] = useState(null);
  const [backup, setBackup] = useState(null);
  const [errors, setErrors] = useState(null);     // Faz AE: error log
  const [heartbeat, setHeartbeat] = useState(null); // Faz AE: worker heartbeat
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // 4 endpoint paralel — biri patlasa diğerleri yine yüklensin
      const [statusR, backupR, errorsR, heartbeatR] = await Promise.allSettled([
        API.get("/api/admin/system/status"),
        API.get("/api/admin/system/backup-status"),
        API.get("/api/admin/system/errors?limit=20"),
        API.get("/api/admin/system/heartbeat")
      ]);
      setStatus(statusR.status === "fulfilled" ? statusR.value.data : null);
      setBackup(backupR.status === "fulfilled" ? backupR.value.data : null);
      setErrors(errorsR.status === "fulfilled" ? errorsR.value.data : null);
      setHeartbeat(heartbeatR.status === "fulfilled" ? heartbeatR.value.data : null);
      setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };
  // async load → cleanup'ta Promise çağrılmasın (arrow wrap)
  useEffect(() => { load(); }, []);

  // Manuel backup tetik
  const runBackupNow = async () => {
    if (!window.confirm("Şimdi yedek alınsın mı? mysqldump 1-3 dakika sürer.")) return;
    setRunning(true);
    try {
      const r = await API.post("/api/admin/system/backup-now");
      if (r.data?.success) {
        toast.success(`✓ Yedek alındı: ${fmtBytes(r.data.sizeBytes)} (${fmtDuration(r.data.durationMs)})`);
      } else {
        toast.error(`Yedek hatası: ${r.data?.error || "bilinmeyen"}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading && !status && !backup) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }

  // Backup durumu özet
  const lastSuccess = backup?.last_success;
  const daysSinceLastBackup = lastSuccess
    ? Math.floor((Date.now() - new Date(lastSuccess.created_at)) / 86400000)
    : null;
  // Backup sağlık seviyesi: 0g=ok, 1-2g=uyarı, 3+g=kritik
  const backupHealth = lastSuccess == null ? "critical"
    : daysSinceLastBackup === 0 ? "ok"
    : daysSinceLastBackup <= 2 ? "warning" : "critical";
  const backupHealthColor = backupHealth === "ok" ? "#10b981"
    : backupHealth === "warning" ? "#f59e0b" : "#ef4444";

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>System Health</Typography>
          <Typography variant="body2" color="text.secondary">
            Platform sağlığı + yedekleme durumu — felaket kurtarma için kritik
          </Typography>
        </Box>
        <Tooltip title="Yenile">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* BACKUP DURUMU — sayfanın en kritik bilgisi, en üste */}
      <Card sx={{
        p: 3, borderRadius: 3, mb: 3,
        border: `2px solid ${backupHealthColor}`,
        background: `linear-gradient(135deg, ${backupHealthColor}11 0%, ${backupHealthColor}05 100%)`
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <BackupIcon sx={{ color: backupHealthColor, fontSize: 36 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {backupHealth === "ok" ? "✅ Yedekleme Sağlıklı" :
               backupHealth === "warning" ? "⚠️ Yedek Eski" :
               "🔴 Yedek Yok / Kritik"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Son başarılı yedek: <strong>{lastSuccess ? fmtDate(lastSuccess.created_at) : "hiç yok"}</strong>
              {daysSinceLastBackup != null && ` (${daysSinceLastBackup} gün önce)`}
            </Typography>
          </Box>
          <Button
            variant="contained" startIcon={<RestartAltIcon />}
            onClick={runBackupNow} disabled={running}
            sx={{ bgcolor: backupHealthColor }}
          >
            {running ? "Alınıyor..." : "Şimdi Yedekle"}
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>BAŞARILI (30g)</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">{backup?.success_count_30d || 0}</Typography>
            <Typography variant="caption" color="text.disabled">/30 hedef</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>HATALI (30g)</Typography>
            <Typography variant="h4" fontWeight={800} color={backup?.fail_count_30d > 0 ? "#ef4444" : "#94a3b8"}>
              {backup?.fail_count_30d || 0}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>TOPLAM BOYUT</Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0">{fmtBytes(backup?.disk_total_bytes)}</Typography>
            <Typography variant="caption" color="text.disabled">{backup?.on_disk?.length || 0} dosya</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>SON YEDEK BOYUTU</Typography>
            <Typography variant="h4" fontWeight={800}>
              {fmtBytes(lastSuccess?.size_bytes)}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {fmtDuration(lastSuccess?.duration_ms)} sürdü
            </Typography>
          </Grid>
        </Grid>
      </Card>

      {/* Son 30 gün backup tablosu */}
      <Card sx={{ borderRadius: 3, mb: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="h6" fontWeight={700}>Son 30 Gün Yedek Geçmişi</Typography>
          <Typography variant="caption" color="text.disabled">
            Dizin: <code>{backup?.backup_dir}</code> · 30 günden eskiler otomatik silinir
          </Typography>
        </Box>
        {(!backup?.logs || backup.logs.length === 0) ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="warning">
              Henüz yedek alınmadı. <strong>"Şimdi Yedekle"</strong> butonuna basarak ilk yedeği oluşturabilirsin.
              Otomatik tetik gece 03:00.
            </Alert>
          </Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>Tarih</strong></TableCell>
                <TableCell><strong>Dosya</strong></TableCell>
                <TableCell align="right"><strong>Boyut</strong></TableCell>
                <TableCell align="right"><strong>Süre</strong></TableCell>
                <TableCell align="center"><strong>Durum</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backup.logs.map((l, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontSize: 12 }}>{fmtDate(l.created_at)}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>
                    {l.filename || <span style={{ color: "#ef4444" }}>(başarısız)</span>}
                  </TableCell>
                  <TableCell align="right">{fmtBytes(l.size_bytes)}</TableCell>
                  <TableCell align="right">{fmtDuration(l.duration_ms)}</TableCell>
                  <TableCell align="center">
                    {l.success ? (
                      <CheckCircleIcon sx={{ color: "#10b981", fontSize: 18 }} />
                    ) : (
                      <Tooltip title={l.error_message || "bilinmeyen hata"}>
                        <CancelIcon sx={{ color: "#ef4444", fontSize: 18 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Sistem entegrasyon durumu (mevcut /status endpoint) */}
      {status && (
        <Card sx={{ borderRadius: 3, mb: 3, overflow: "hidden" }}>
          <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
            <StorageIcon sx={{ color: "#1565C0" }} />
            <Typography variant="h6" fontWeight={700}>Entegrasyon Durumu</Typography>
          </Box>
          <Grid container spacing={1.5} sx={{ p: 2 }}>
            {(status.integrations || []).map((int, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card variant="outlined" sx={{
                  p: 1.5,
                  borderLeft: int.active ? "3px solid #10b981" : "3px solid #94a3b8",
                  bgcolor: int.active ? "#f0fdf4" : "#f8fafc"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    {int.active ? (
                      <CheckCircleIcon sx={{ color: "#10b981", fontSize: 16 }} />
                    ) : (
                      <CancelIcon sx={{ color: "#94a3b8", fontSize: 16 }} />
                    )}
                    <Typography fontWeight={700} fontSize={13}>{int.name}</Typography>
                    <Box sx={{ flex: 1 }} />
                    {int.priority && (
                      <Chip label={int.priority} size="small" sx={{ height: 18, fontSize: 10 }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontSize={11}>
                    {int.impact}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Card>
      )}

      {/* Worker durumu */}
      {status?.workers && (
        <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <Typography variant="h6" fontWeight={700}>Worker Servisleri</Typography>
          </Box>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>Worker</strong></TableCell>
                <TableCell><strong>Açıklama</strong></TableCell>
                <TableCell align="center"><strong>Durum</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {status.workers.map((w, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>{w.name}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{w.description}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={w.active ? "Aktif" : "Pasif"}
                      size="small"
                      sx={{
                        bgcolor: w.active ? "#10b981" : "#94a3b8",
                        color: "white", fontWeight: 700, fontSize: 11
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* ────────────────────────────────────────────────────────────────
          Faz AE: Worker Heartbeat panel
          ──────────────────────────────────────────────────────────────── */}
      {heartbeat?.workers && heartbeat.workers.length > 0 && (
        <Card sx={{ borderRadius: 3, mb: 3, overflow: "hidden" }}>
          <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
            <MonitorHeartIcon sx={{ color: "#10b981" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={700}>Worker Heartbeat</Typography>
              <Typography variant="caption" color="text.disabled">
                Her worker'ın son tick zamanı + sağlık durumu (90dk+ tick atmamış = stale uyarı)
              </Typography>
            </Box>
          </Box>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>Worker</strong></TableCell>
                <TableCell><strong>Son Tick</strong></TableCell>
                <TableCell align="center"><strong>Toplam Tick</strong></TableCell>
                <TableCell align="center"><strong>Hatalar</strong></TableCell>
                <TableCell align="center"><strong>Sağlık</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {heartbeat.workers.map(w => {
                // Sağlık seviyesi → renk
                const healthColor = w.health === "ok" ? "#10b981"
                  : w.health === "warn" ? "#f59e0b"
                  : w.health === "stale" ? "#f97316" : "#ef4444";
                const healthLabel = w.health === "ok" ? "Sağlıklı"
                  : w.health === "warn" ? "Uyarı"
                  : w.health === "stale" ? "Eski Tick" : "Kritik";
                return (
                  <TableRow key={w.worker_name} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{w.worker_name}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {fmtDate(w.last_tick_at)}
                      <Typography variant="caption" color="text.disabled" sx={{ display: "block", fontSize: 10 }}>
                        {w.minutes_since_last_tick} dk önce
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{w.total_ticks}</TableCell>
                    <TableCell align="center">
                      {w.total_fails > 0 ? (
                        <Typography color="#ef4444" fontWeight={700}>
                          {w.total_fails} {w.consecutive_fails > 0 && `(arka arkaya ${w.consecutive_fails})`}
                        </Typography>
                      ) : (
                        <Typography color="#10b981">0</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={healthLabel}
                        size="small"
                        sx={{ bgcolor: healthColor, color: "white", fontWeight: 700, fontSize: 11 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* ────────────────────────────────────────────────────────────────
          Faz AE: Error Log panel
          ──────────────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 3, mb: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2, bgcolor: errors?.errors?.length > 0 ? "#fef2f2" : "#f0fdf4",
                   borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
          <BugReportIcon sx={{ color: errors?.errors?.length > 0 ? "#ef4444" : "#10b981" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>Hata Logu (son 20)</Typography>
            <Typography variant="caption" color="text.disabled">
              5xx HTTP hataları + worker exception'ları otomatik buraya düşer
            </Typography>
          </Box>
          {errors?.by_source?.length > 0 && (
            <Chip
              label={`${errors.by_source.reduce((s, e) => s + Number(e.cnt), 0)} hata (24h)`}
              size="small"
              sx={{ bgcolor: "#ef4444", color: "white", fontWeight: 700 }}
            />
          )}
        </Box>
        {!errors?.errors || errors.errors.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="success">
              ✅ Hata yok — sistem temiz.
              {errors?.errors?.length === 0 && " (error_log tablosu boş)"}
            </Alert>
          </Box>
        ) : (
          <>
            {/* Kaynak bazlı 24h özet */}
            {errors.by_source && errors.by_source.length > 0 && (
              <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", flexWrap: "wrap", gap: 1 }}>
                {errors.by_source.map((s, i) => (
                  <Chip
                    key={i}
                    label={`${s.source}: ${s.cnt} ${s.level}`}
                    size="small"
                    sx={{
                      bgcolor: s.level === "error" ? "#ef4444" : s.level === "warn" ? "#f59e0b" : "#3b82f6",
                      color: "white",
                      fontWeight: 600
                    }}
                  />
                ))}
              </Box>
            )}
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell width={140}><strong>Zaman</strong></TableCell>
                  <TableCell width={120}><strong>Kaynak</strong></TableCell>
                  <TableCell width={70}><strong>Seviye</strong></TableCell>
                  <TableCell><strong>Hata</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.errors.map(e => (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ fontSize: 11 }}>{fmtDate(e.created_at)}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>{e.source}</TableCell>
                    <TableCell>
                      <Chip
                        label={e.level}
                        size="small"
                        sx={{
                          bgcolor: e.level === "error" ? "#ef4444" : e.level === "warn" ? "#f59e0b" : "#3b82f6",
                          color: "white", fontWeight: 600, fontSize: 10
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      <Typography fontSize={12} fontWeight={600} sx={{ wordBreak: "break-word" }}>
                        {e.message}
                      </Typography>
                      {e.request_url && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: "block", fontSize: 10, fontFamily: "monospace" }}>
                          {e.request_url}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Card>

      {/* Faz AJ: Off-site backup durumu */}
      {backup?.offsite && (
        <Card sx={{
          p: 2.5, borderRadius: 3, mb: 3,
          bgcolor: backup.offsite.configured ? "#f0fdf4" : "#fff7ed",
          border: backup.offsite.configured ? "1px solid #bbf7d0" : "1px solid #fed7aa"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <BackupIcon sx={{ color: backup.offsite.configured ? "#10b981" : "#f97316", fontSize: 24 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {backup.offsite.configured
                  ? `✅ Off-site Backup Aktif: ${backup.offsite.type}`
                  : "⚠️ Off-site Backup Yok"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {backup.offsite.configured
                  ? `Yedekler her gün ${backup.offsite.type} cloud'una kopyalanır. VPS ölse bile yedek dış sunucuda durur.`
                  : "Yedekler sadece bu VPS'te. VPS tamamen ölürse yedek de gider. Off-site setup için aşağıdaki rehbere bak."}
              </Typography>
            </Box>
          </Box>
          {!backup.offsite.configured && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: "white", borderRadius: 1.5, fontFamily: "monospace", fontSize: 11 }}>
              <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: 700, color: "#1f2937" }}>
                Setup adımı (VPS .env dosyasına ekle, sonra docker restart):
              </Typography>
              <Box sx={{ color: "#1565C0" }}>
                # Google Drive (rclone gerekli):<br/>
                OFFSITE_BACKUP_CMD="rclone copy {`{file}`} mygdrive:miksan-backups"<br/>
                <br/>
                # Backblaze B2 (en uygun fiyat $0.005/GB):<br/>
                OFFSITE_BACKUP_CMD="rclone copy {`{file}`} b2remote:miksan-backups"<br/>
                <br/>
                # İkinci VPS/sunucu (SSH key gerekli):<br/>
                OFFSITE_BACKUP_CMD="scp {`{file}`} backup@otherhost:/backups/"
              </Box>
            </Box>
          )}
        </Card>
      )}

      {/* Felaket kurtarma rehberi */}
      <Card sx={{ p: 2, borderRadius: 3, mt: 3, bgcolor: "#f8fafc" }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>📚 Felaket Kurtarma Rehberi</Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          <strong>1) DB bozulursa:</strong> En son <code>db-*.sql.gz</code> dosyasını <code>/opt/backups/</code>'tan al.
          Restore: <code>gunzip -c db-XXX.sql.gz | docker exec -i ambalaj-mysql mysql -uroot -p$PASS ambalaj_db</code><br/>
          <strong>2) VPS down olursa:</strong> Yeni VPS'e docker-compose ile platform yükle, son backup'tan restore yap. Tahmini downtime: 30dk-2sa.<br/>
          <strong>3) Hatalı silme:</strong> Bir gün öncesinden restore et veya tek tablo restore (sql dosyasını tablo başlığına göre kes).<br/>
          <strong>Off-site backup önerisi:</strong> S3/Backblaze/rsync.net gibi bir servise haftalık otomatik kopya. Yedek dosyaları aynı VPS'te durursa VPS gidince yedek de gider.
        </Typography>
      </Card>
    </Box>
  );
}
